export function normalizeSize(value) {
  const matches = [...String(value || '').normalize('NFKC').toLowerCase().matchAll(/(?:^|[^\d])(\d{2,3})\s*(?:cm\s*)?[x×*]\s*(\d{2,3})\s*(?:cm)?(?=$|[^\d])/g)];
  const sizes = [...new Set(matches.map(match => `${Number(match[1])}x${Number(match[2])}`))];
  return sizes.length === 1 ? sizes[0] : null;
}
export function eligibleMapping(mapping, bed, fallbackSize = '') {
  if (!mapping || !bed || mapping.confirmed !== true || mapping.available !== true || bed.available !== true) return false;
  if (!Number.isInteger(mapping.price) || mapping.price <= 0 || !Number.isInteger(bed.price) || bed.price <= 0) return false;
  if (!/^\d+$/.test(String(mapping.id)) || String(mapping.id) === String(bed.id)) return false;
  const bedSize = normalizeSize(bed.title) || normalizeSize(fallbackSize);
  const mappingSize = normalizeSize(mapping.size);
  const mattressSize = normalizeSize(mapping.title);
  return !!bedSize && mappingSize === bedSize && (!mattressSize || mattressSize === bedSize);
}
export function preparationAllowed(allowedSizes, bed, fallbackSize = '') {
  const sizes = String(allowedSizes || '').split(',').filter(Boolean);
  const bedSize = normalizeSize(bed?.title) || normalizeSize(fallbackSize);
  return !sizes.length || !bedSize || sizes.includes(bedSize);
}
export function preparationVariant(variants, bed, fallbackSize = '') {
  const size = normalizeSize(bed?.title) || normalizeSize(fallbackSize);
  if (!size || !Array.isArray(variants)) return null;
  const matches = variants.filter(variant => normalizeSize(variant.title) === size && Number.isInteger(variant.price) && variant.price > 0);
  return matches.length === 1 ? matches[0] : null;
}
export function packItems(bed, mapping, quantity, fallbackSize = '') {
  if (!Number.isSafeInteger(quantity) || quantity < 1 || !eligibleMapping(mapping, bed, fallbackSize)) return null;
  return [{ id: String(bed.id), quantity }, { id: String(mapping.id), quantity }];
}
export async function addPack(fetcher, url, items, genericError) {
  const response = await fetcher(url, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
  let result;
  try { result = await response.json(); } catch { throw new Error(genericError); }
  if (!response.ok || !result || result.status >= 400) {
    const detail = typeof result?.description === 'string' ? result.description : typeof result?.message === 'string' ? result.message : genericError;
    throw new Error(detail);
  }
  if (!Array.isArray(result.items) || items.some(item => !result.items.some(added => String(added.variant_id || added.id) === item.id && Number(added.quantity) >= item.quantity))) throw new Error(genericError);
  return result;
}
if (typeof customElements !== 'undefined' && !customElements.get('almeora-mattress-pack')) {
  customElements.define('almeora-mattress-pack', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.form = this.closest('form');
      if (!this.form) return;
      try { this.beds = JSON.parse(this.querySelector('[data-pack-beds]').textContent); } catch { return; }
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.choices = [...this.querySelectorAll('[data-pack-choice]')];
      this.preparation = [...this.querySelectorAll('[data-pack-preparation]')];
      this.preparationVariants = new Map(this.preparation.map(input => {
        try { return [input, JSON.parse(input.closest('[data-pack-preparation-row]').querySelector('[data-pack-mattress-variants]').textContent)]; }
        catch { return [input, []]; }
      }));
      this.buttonOriginals = new Map();
      this.alone = this.choices.find(choice => choice.value === '');
      this.rows = this.choices.filter(choice => choice.value !== '').map(input => ({ input, row: input.closest('[data-pack-option]'), mapping: { id: input.value, size: input.dataset.size, title: input.dataset.variantTitle, price: Number(input.dataset.cents), confirmed: input.dataset.confirmed === 'true', available: input.dataset.available === 'true' } }));
      this.form.addEventListener('change', event => {
        if (event.target.matches('[data-pack-mode]')) {
          this.rows.forEach(row => row.input.checked = false);
          this.preparation.forEach(input => input.checked = false);
          this.withMattress = event.target.value === 'with';
        }
        this.refresh(); queueMicrotask(() => { this.updateButtons(); this.syncStickyTotal(); }); }, options);
      this.form.addEventListener('input', event => { if (event.target.name === 'quantity') this.refresh(); }, options);
      this.form.addEventListener('submit', event => this.submit(event), { ...options, capture: true });
      this.refresh();
    }
    disconnectedCallback() { this.restoreButtons(); this.controller?.abort(); this.controller = null; }
    bed() {
      const selected = this.form.querySelector('input[name="id"]:checked') || this.form.querySelector('select[name="id"]') || this.form.querySelector('input[name="id"][type="hidden"]');
      return this.beds.find(bed => String(bed.id) === selected?.value);
    }
    quantity() { return Number(this.form.querySelector('[name="quantity"]')?.value || 1); }
    enquiry() { return this.preparation.find(input => input.checked); }
    buttons() { return [...this.form.elements].filter(control => control.type === 'submit'); }
    restoreButtons() {
      this.buttonOriginals?.forEach((state, button) => { button.innerHTML = state.html; button.disabled = state.disabled; });
      this.buttonOriginals?.clear();
    }
    updateButtons() {
      if (this.withMattress && !this.enquiry() && !this.selected()) {
        this.buttons().forEach(button => {
          if (!this.buttonOriginals.has(button)) this.buttonOriginals.set(button, {html:button.innerHTML,disabled:button.disabled});
          const label=button.querySelector('[data-buy-text]');
          if(label) label.textContent=this.dataset.chooseMattress; else button.textContent=this.dataset.chooseMattress;
          button.disabled=true;
        });
      } else if (this.enquiry()) {
        this.buttons().forEach(button => {
          if (!this.buttonOriginals.has(button)) this.buttonOriginals.set(button, { html: button.innerHTML, disabled: button.disabled });
          const label = button.querySelector('[data-buy-text]');
          if (label) label.textContent = this.dataset.enquiryButton;
          else button.textContent = this.dataset.enquiryButton;
          button.disabled = false;
        });
      } else {
        this.restoreButtons();
        const bed = this.bed();
        this.buttons().forEach(button => { button.disabled = bed?.available !== true; });
      }
    }
    selected() { return this.rows.find(row => row.input.checked); }
    refresh() {
      if (this.pending || this.locked) return;
      this.withMattress = !!this.querySelector('[data-pack-mode][value="with"]:checked');
      const bed = this.bed();
      let count = 0;
      this.rows.forEach(row => {
        const eligible = eligibleMapping(row.mapping, bed, this.dataset.fallbackSize);
        row.row.hidden = !eligible || !this.withMattress;
        row.input.disabled = !eligible || !this.withMattress;
        if (eligible) count++;
        else if (row.input.checked) this.alone.checked = true;
      });
      const preparationPanel = this.querySelector('[data-pack-preparation-panel]');
      if (preparationPanel) preparationPanel.hidden = count > 0;
      let preparationCount = 0;
      this.preparation.forEach(input => {
        const variant = preparationVariant(this.preparationVariants.get(input), bed, this.dataset.fallbackSize);
        const allowed = this.withMattress && count === 0 && !!variant;
        input.disabled = !allowed;
        const family = this.querySelector('[data-pack-family]')?.value || 'all';
        const row = input.closest('[data-pack-preparation-row]');
        input.dataset.cents = String(variant?.price || 0);
        input.dataset.variantId = String(variant?.id || '');
        input.dataset.title = variant ? `${input.dataset.modelTitle} · ${variant.title}` : input.dataset.modelTitle;
        input.dataset.url = variant ? `${input.dataset.productUrl}?variant=${variant.id}` : input.dataset.productUrl;
        row.querySelector('[data-pack-preparation-price]').textContent = variant ? `${variant.title} · ${this.money(variant.price)}` : '';
        row.querySelector('[data-pack-preparation-link]').href = input.dataset.url;
        row.hidden = !allowed || (!input.checked && family !== 'all' && row.dataset.family !== family);
        if (count === 0 && variant) preparationCount++;
        else if (input.checked) this.alone.checked = true;
      });
      if (this.withMattress && this.alone.checked) {this.withMattress=false;this.refresh();return;}
      if (preparationPanel) preparationPanel.hidden = !this.withMattress || count > 0 || preparationCount === 0;
      if (preparationPanel) preparationPanel.open = this.withMattress;
      this.hidden = count === 0 && preparationCount === 0;
      this.alone.disabled = this.hidden;
      this.querySelector('[data-pack-mode][value="with"]').disabled = this.hidden;
      if (this.hidden) this.alone.checked = true;
      const quantity = this.quantity();
      const cents = bed?.price + (this.selected()?.mapping.price || Number(this.enquiry()?.dataset.cents) || 0);
      const total = Number.isSafeInteger(quantity) && quantity > 0 && Number.isFinite(cents) ? cents * quantity : 0;
      this.querySelector('[data-pack-total-label]').textContent = this.enquiry() ? this.dataset.indicativeLabel : this.dataset.totalLabel;
      this.querySelector('[data-pack-selection]').textContent = `${this.dataset.bedTitle} · ${bed?.title === 'Default Title' ? this.dataset.fallbackSize : bed?.title || ''}${this.enquiry() ? ' + ' + this.enquiry().dataset.title : this.selected() ? ' + ' + this.selected().input.closest('label').querySelector('strong').textContent : ''}`;
      this.updateButtons();
      this.querySelector('[data-pack-total]').textContent = this.money(total);
      this.syncStickyTotal();
    }
    money(cents) { return new Intl.NumberFormat(this.dataset.locale || 'fr', { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(cents / 100); }
    syncStickyTotal() {
      const sticky = this.closest('.alx')?.querySelector('.sticky-buy');
      if (!sticky) return;
      const price = sticky.querySelector('[data-price]');
      const label = sticky.querySelector('[data-sticky-total-label]');
      if (price) price.textContent = this.querySelector('[data-pack-total]').textContent;
      if (label) label.textContent = this.querySelector('[data-pack-total-label]').textContent;
    }
    async submit(event) {
      if (this.pending || this.locked) { event.preventDefault(); event.stopImmediatePropagation(); return; }
      if (this.withMattress && !this.enquiry() && !this.selected()) {event.preventDefault();event.stopImmediatePropagation();this.showError(this.dataset.invalid);return;}
      const enquiry = this.enquiry();
      if (enquiry) {
        event.preventDefault(); event.stopImmediatePropagation();
        const bed = this.bed();
        const variant = preparationVariant(this.preparationVariants.get(enquiry), bed, this.dataset.fallbackSize);
        if (enquiry.disabled || !variant || String(variant.id) !== enquiry.dataset.variantId) { this.alone.checked = true; this.refresh(); return; }
        const size = normalizeSize(bed?.title) || normalizeSize(this.dataset.fallbackSize) || '';
        const total = this.querySelector('[data-pack-total]').textContent;
        const composition = `${this.quantity()} × ${this.dataset.bedTitle}${size ? ` (${size} cm)` : ''} + ${this.quantity()} × ${enquiry.dataset.title}`;
        const url = new URL(this.dataset.contactUrl, window.location.origin);
        url.hash = 'demande-ensemble';
        url.searchParams.set('ensemble', `${this.dataset.enquiryMessage}\n${composition}\n${this.dataset.indicativeLabel} : ${total}\n${window.location.origin}${enquiry.dataset.url}`.slice(0, 3000));
        window.location.assign(url.href);
        return;
      }
      const chosen = this.selected();
      if (!chosen || this.hidden) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const items = packItems(this.bed(), chosen.mapping, this.quantity(), this.dataset.fallbackSize);
      if (!items) { this.showError(this.dataset.invalid); this.refresh(); return; }
      this.pending = true;
      this.setAttribute('aria-busy', 'true');
      this.form.querySelectorAll('input,select,button[type="submit"]').forEach(control => { control.dataset.packDisabledBefore = String(control.disabled); control.disabled = true; });
      try {
        await addPack(window.fetch.bind(window), this.dataset.addUrl, items, this.dataset.genericError);
        this.locked = true;
        window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        this.locked = true;
        this.showError(`${error.message || this.dataset.genericError} ${this.dataset.review}`);
      } finally {
        this.pending = false;
        this.removeAttribute('aria-busy');
        if (!this.locked) this.form.querySelectorAll('[data-pack-disabled-before]').forEach(control => { control.disabled = control.dataset.packDisabledBefore === 'true'; delete control.dataset.packDisabledBefore; });
      }
    }
    showError(message) {
      const error = this.querySelector('[data-pack-error]');
      error.textContent = message;
      error.parentElement.hidden = false;
      error.focus();
    }
  });
}



